import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, Loader2, MoreHorizontal, X } from "lucide-react";
import ActionSheet, { ActionSheetItem } from "./action-sheet";
import MobileIconButton from "./mobile-icon-button";
import { cn } from "../../lib/utils";

/** Field styling for inputs rendered inside mobile flow cards. */
export const MOBILE_FIELD_CLASS = "h-11 rounded-xl bg-white text-[15px]";

export interface MobileFlowStep {
  id: string;
  label: string;
}

export interface MobileFlowPrimaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
}

export interface MobileFlowLayoutProps {
  title: string;
  steps: MobileFlowStep[];
  currentStepIndex: number;
  /** Leaves the flow (same destination as the desktop close/cancel button). */
  onClose: () => void;
  closeIcon?: "back" | "close";
  actions?: Array<ActionSheetItem | false | null | undefined>;
  /** Omit to hide the secondary button (e.g. on the first step). */
  onBack?: () => void;
  backLabel?: string;
  primaryAction: MobileFlowPrimaryAction;
  children: React.ReactNode;
}

const MobileFlowLayout: React.FC<MobileFlowLayoutProps> = ({
  title,
  steps,
  currentStepIndex,
  onClose,
  closeIcon = "back",
  actions,
  onBack,
  backLabel = "Back",
  primaryAction,
  children,
}) => {
  const [actionsOpen, setActionsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const previousStepRef = useRef(currentStepIndex);
  const direction = currentStepIndex >= previousStepRef.current ? 1 : -1;

  useEffect(() => {
    previousStepRef.current = currentStepIndex;
    scrollRef.current?.scrollTo({ top: 0 });
  }, [currentStepIndex]);

  const visibleActions = (actions ?? []).filter(
    (action): action is ActionSheetItem => !!action,
  );
  const safeIndex = Math.min(
    Math.max(currentStepIndex, 0),
    Math.max(steps.length - 1, 0),
  );
  const currentStep = steps[safeIndex];
  const progress =
    steps.length > 0 ? ((safeIndex + 1) / steps.length) * 100 : 0;
  const isPrimaryDisabled = primaryAction.disabled || primaryAction.loading;

  return (
    <div className="flex h-full min-w-0 flex-col bg-gray-50">
      <header className="relative z-20 shrink-0 border-b border-gray-100 bg-white pt-[env(safe-area-inset-top)]">
        <div className="flex h-14 items-center gap-2 px-3">
          <MobileIconButton
            label={closeIcon === "close" ? "Close" : "Back"}
            variant="plain"
            onClick={onClose}
          >
            {closeIcon === "close" ? (
              <X className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-6 w-6" />
            )}
          </MobileIconButton>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-bold leading-tight font-jakarta text-gray-900">
              {title}
            </h1>
            {currentStep && (
              <p className="truncate text-xs text-gray-500">
                Step {safeIndex + 1} of {steps.length} · {currentStep.label}
              </p>
            )}
          </div>
          {visibleActions.length > 0 ? (
            <MobileIconButton
              label="More actions"
              variant="plain"
              onClick={() => setActionsOpen(true)}
            >
              <MoreHorizontal className="h-5 w-5" />
            </MobileIconButton>
          ) : (
            <span className="h-10 w-10 shrink-0" aria-hidden="true" />
          )}
        </div>
        <div
          className="h-0.5 w-full bg-gray-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={steps.length}
          aria-valuenow={safeIndex + 1}
        >
          <div
            className="h-full rounded-r-full bg-primary-600 transition-[width] duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain"
      >
        <motion.div
          key={currentStep?.id ?? safeIndex}
          initial={{ opacity: 0, x: 16 * direction }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="space-y-4 p-4 pb-6 text-sm text-gray-800"
        >
          {children}
        </motion.div>
      </div>

      <footer className="shrink-0 border-t border-gray-100 bg-white px-4 pt-3 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]">
        <div className="flex gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              disabled={primaryAction.loading}
              className="h-12 flex-1 rounded-2xl border-0 bg-gray-100 text-sm font-semibold text-gray-800 active:bg-gray-200 disabled:opacity-50"
            >
              {backLabel}
            </button>
          )}
          <button
            type="button"
            onClick={primaryAction.onClick}
            disabled={isPrimaryDisabled}
            className={cn(
              "flex h-12 items-center justify-center gap-2 rounded-2xl border-0 bg-primary-600 px-4 text-sm font-semibold text-white active:bg-primary-700 disabled:opacity-50",
              onBack ? "flex-[1.6]" : "w-full",
            )}
          >
            {primaryAction.loading && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            <span className="truncate">
              {primaryAction.loading && primaryAction.loadingLabel
                ? primaryAction.loadingLabel
                : primaryAction.label}
            </span>
          </button>
        </div>
      </footer>

      {visibleActions.length > 0 && (
        <ActionSheet
          open={actionsOpen}
          onClose={() => setActionsOpen(false)}
          title={title}
          actions={visibleActions}
        />
      )}
    </div>
  );
};

interface MobileFlowCardProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

export const MobileFlowCard = React.forwardRef<
  HTMLElement,
  MobileFlowCardProps
>(({ title, description, action, className, children }, ref) => (
  <section
    ref={ref}
    className={cn(
      "min-w-0 scroll-mt-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_1px_2px_rgba(13,14,19,0.04)]",
      className,
    )}
  >
    {(title || action) && (
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          {title && (
            <h2 className="text-[15px] font-semibold leading-tight text-gray-900">
              {title}
            </h2>
          )}
          {description && (
            <p className="mt-0.5 text-xs text-gray-500">{description}</p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    )}
    {children}
  </section>
));
MobileFlowCard.displayName = "MobileFlowCard";

export const MobileFlowField: React.FC<{
  label: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, htmlFor, hint, children }) => (
  <div className="min-w-0 space-y-1.5">
    <label
      htmlFor={htmlFor}
      className="block text-[13px] font-medium text-gray-700"
    >
      {label}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
  </div>
);

export default MobileFlowLayout;
